import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  RefreshCw, Package, ArrowLeft, Clock, CheckCircle, 
  XCircle, Truck, Eye, MoreHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatPrice } from '@/lib/utils';

const statusConfig = {
  requested: { label: 'Requested', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  label_sent: { label: 'Label Sent', color: 'bg-purple-100 text-purple-800', icon: Package },
  in_transit: { label: 'In Transit', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  received: { label: 'Received', color: 'bg-teal-100 text-teal-800', icon: Package },
  inspecting: { label: 'Inspecting', color: 'bg-orange-100 text-orange-800', icon: Eye },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800', icon: XCircle }
};

export default function Returns() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchReturns();
  }, [statusFilter]);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      const res = await api.get(`/enterprise/returns?${params}`);
      setReturns(res.data.returns);
    } catch (error) {
      toast.error('Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  const updateReturnStatus = async (returnId, newStatus) => {
    try {
      await api.put(`/enterprise/returns/${returnId}`, { status: newStatus });
      toast.success('Return status updated');
      fetchReturns();
    } catch (error) {
      toast.error('Failed to update return');
    }
  };

  return (
    <div className="min-h-screen pb-16" data-testid="returns-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium text-stone-900">Returns (RMA)</h1>
            <p className="text-stone-500 mt-1">Manage return requests and refunds</p>
          </div>
          <Button variant="outline" onClick={fetchReturns}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {['requested', 'approved', 'in_transit', 'inspecting', 'completed'].map((status) => {
            const count = returns.filter(r => r.status === status).length;
            const config = statusConfig[status];
            return (
              <div 
                key={status}
                className={`p-4 rounded-lg border cursor-pointer ${
                  statusFilter === status ? 'ring-2 ring-stone-900' : ''
                }`}
                onClick={() => setStatusFilter(status === statusFilter ? 'all' : status)}
              >
                <p className="text-2xl font-semibold">{count}</p>
                <p className="text-sm text-stone-500">{config.label}</p>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Returns</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Return ID</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Refund</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={8}>
                      <div className="h-12 skeleton rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : returns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <Package className="h-12 w-12 mx-auto text-stone-300 mb-4" />
                    <p className="text-stone-500">No returns found</p>
                  </TableCell>
                </TableRow>
              ) : (
                returns.map((ret) => {
                  const config = statusConfig[ret.status] || statusConfig.requested;
                  const StatusIcon = config.icon;
                  return (
                    <TableRow key={ret.return_id}>
                      <TableCell className="font-mono text-sm">
                        {ret.return_id}
                      </TableCell>
                      <TableCell>
                        <Link 
                          to={`/admin/orders/${ret.order_id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {ret.order_id}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {ret.items?.length || 0} item(s)
                      </TableCell>
                      <TableCell className="capitalize">
                        {ret.reason?.replace('_', ' ')}
                      </TableCell>
                      <TableCell>
                        <Badge className={config.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {config.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {ret.refund_amount > 0 ? formatPrice(ret.refund_amount) : '-'}
                      </TableCell>
                      <TableCell className="text-stone-500">
                        {new Date(ret.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => updateReturnStatus(ret.return_id, 'approved')}>
                              <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateReturnStatus(ret.return_id, 'rejected')}>
                              <XCircle className="h-4 w-4 mr-2 text-red-600" />
                              Reject
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateReturnStatus(ret.return_id, 'label_sent')}>
                              <Package className="h-4 w-4 mr-2" />
                              Send Label
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateReturnStatus(ret.return_id, 'received')}>
                              <Truck className="h-4 w-4 mr-2" />
                              Mark Received
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateReturnStatus(ret.return_id, 'completed')}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Complete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
